import { createPortal } from "react-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import { useDispatch, useSelector } from "react-redux";
import {
    CheckIcon,
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ClipboardDocumentListIcon,
    CubeIcon,
    MagnifyingGlassIcon,
    PencilSquareIcon,
    PlusIcon,
    Squares2X2Icon,
    TrashIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import axios from "axios";
import Drawer from "../Drawer";
import { AddDrawerSelect } from "../productCatalog/AddDrawerSelect";
import { ConfrimModal } from "../ConfrimModal";
import { LoaderSpinner } from "../LoaderSpinner";
import {
    addBomBoxPreset,
    BomUiLoadingKeys,
    createBomComponentLibraryItem,
    deleteBomComponentLibraryItem,
    deleteBomBoxPresets,
    clearBomFilters,
    clearBomRowSelection,
    fetchBomComponentLibrary,
    fetchBundleBomUi,
    saveBomComponentLibraryItem,
    deletePackagingBom,
    fetchPackagingBomUi,
    fetchProductBomUi,
    deletePackagingInstructions,
    savePackagingBom,
    savePackagingInstructions,
    searchBomProductGroups,
    selectBomUi,
    setActiveBomTab,
    setBomFilter,
    setBomSettingsOpen,
    updateBomBoxPreset,
} from "../../slices/bomUiSlice";
import { useDebounce } from "../../helpers/useDebounce";
import { ProductGroupLoadingKeys, getSuppliers } from "../../slices/productGroupSlice";

const BOX_PRESET_STACK = {
    supplier: (rowIndex) => `box-preset-supplier-${rowIndex}`,
};

const COMPONENT_LIBRARY_STACK = {
    supplier: "bom-component-lib-supplier",
};

const BOM_USAGE_COUNTS_URL = "/api/v1/bill-of-materials/settings/usage-counts";
const BOM_USAGE_SKUS_URL = "/api/v1/bill-of-materials/settings/usage-skus";

const emptyBomUsageCounts = () => ({
    component_preset: {},
    box_preset_group: {},
});

function parseComponentCostInput(raw) {
    const s = String(raw ?? "")
        .trim()
        .replace(/,/g, ".");
    if (!s) return 0;
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
}

const SECTION_TABS = [
    { id: "components", label: "Component Library", Icon: CubeIcon },
    { id: "presets", label: "Box Presets", Icon: Squares2X2Icon },
    { id: "instructions", label: "Instruction Presets", Icon: ClipboardDocumentListIcon },
];

function SearchInput({ value, onChange, placeholder, isComponent = false }) {
    return (
        <div className={`relative ${isComponent ? "w-full" : ""}`}>
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className={`h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-9 text-sm text-gray-700 outline-none transition focus:border-gray-400`}
            />
            {value ? (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                >
                    <XMarkIcon className="h-4 w-4" />
                </button>
            ) : null}
        </div>
    );
}

function FormTextInput({ label, value, onChange, placeholder, type = "text", required = false, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {label}
                {required ? " *" : ""}
            </span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className={`h-9 w-full no-arrows-by-input shadow-sm rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 outline-none transition focus:border-gray-400 ${type === "number" ? "text-right" : ""}`}
            />
        </label>
    );
}

function FormUnitCostInput({ label, value, onChange, placeholder = "0.00", className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
            <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
                    $
                </span>
                <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder || "0.00"}
                    className="h-9 w-full text-right rounded-md border border-gray-200 bg-white py-0 pl-7 pr-3 text-sm text-gray-700 outline-none transition focus:border-gray-400"
                />
            </div>
        </label>
    );
}

function SectionTabBar({ section, onChange }) {
    return (
        <div className="flex border-b border-gray-200">
            {SECTION_TABS.map(({ id, label, Icon }) => {
                const active = section === id;
                return (
                    <button
                        key={id}
                        type="button"
                        onClick={() => onChange(id)}
                        className={[
                            "flex items-center gap-1.5 border-b-2 px-5 py-3 text-sm font-medium transition-colors",
                            active
                                ? "border-gray-900 text-gray-900"
                                : "border-transparent text-gray-500 hover:text-gray-800",
                        ].join(" ")}
                    >
                        <Icon className="h-4 w-4" />
                        {label}
                    </button>
                );
            })}
        </div>
    );
}

function SectionHeader({ 
    countLabel, 
    search, 
    onSearchChange, 
    searchPlaceholder, 
    actionLabel, 
    onAction, 
    isComponent = false 
}) {
    return (
        <>
            {!isComponent && (
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50/60 flex-shrink-0 gap-3">
                    <span className="text-sm text-gray-500">{countLabel}</span>
                    {actionLabel && onAction ? (
                        <button
                            type="button"
                            onClick={onAction}
                            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-gray-900 px-4 text-sm font-medium text-white transition hover:bg-gray-800"
                        >
                            <PlusIcon className="h-4 w-4" />
                            {actionLabel}
                        </button>
                    ) : null}
                </div>
            )}
            <div className={`border-b border-gray-200 bg-white px-4 py-3 ${isComponent ? "flex items-center justify-between gap-2 !bg-gray-50" : ""}`}>
                <SearchInput value={search} onChange={onSearchChange} placeholder={searchPlaceholder} isComponent={isComponent} />
                {isComponent && (
                    <button
                        type="button"
                        onClick={onAction}
                        className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-gray-900 px-4 text-sm font-medium text-white transition hover:bg-gray-800"
                    >
                        <PlusIcon className="h-4 w-4" />
                        {actionLabel}
                    </button>
                )}
            </div>
        </>

    );
}

function InlineSectionShell({ title, onBack, children }) {
    return (
        <div className="flex h-full flex-col bg-white">
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
                <button
                    type="button"
                    onClick={onBack}
                    className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                >
                    <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="text-sm font-semibold text-gray-900">{title}</span>
            </div>
            <div className="flex-1 overflow-y-auto flex flex-col justify-between">{children}</div>
        </div>
    );
}

function ComponentEditorSection({
    draft,
    onPatchDraft,
    onSave,
    onCancel,
    vendorOptions,
    vendorSelectLoading,
    openSelectKey,
    onOpenSelectKeyChange,
    isSaving,
}) {
    return (
        <InlineSectionShell title={draft.id ? "Edit Component Preset" : "New Component"} onBack={onCancel}>
            <div className="space-y-4 px-4 py-4">
                <FormTextInput
                    label="Name"
                    value={draft.name}
                    onChange={(value) => onPatchDraft({ name: value })}
                    placeholder="e.g. Wallet Sticker"
                    required
                />
                <FormTextInput
                    label="Type / Description"
                    value={draft.type}
                    onChange={(value) => onPatchDraft({ type: value })}
                    placeholder="e.g. Round Kings Loot Sticker"
                />
                <div className="grid grid-cols-2 gap-3">
                    <FormUnitCostInput
                        label="Unit Cost ($)"
                        value={draft.defaultUnitCost}
                        onChange={(value) => onPatchDraft({ defaultUnitCost: value })}
                        placeholder="0.0000"
                    />
                    <FormTextInput
                        label="Default Qty"
                        type="number"
                        value={draft.defaultQty}
                        onChange={(value) => onPatchDraft({ defaultQty: value })}
                        placeholder="1"
                    />
                </div>
                <div>
                    <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Default Supplier
                    </span>
                    <AddDrawerSelect
                        options={vendorOptions}
                        value={draft.vendorId ?? ""}
                        onChange={(value) => onPatchDraft({ vendorId: value === undefined || value === null ? "" : String(value) })}
                        noneLabel="Select supplier…"
                        allLabel="All suppliers"
                        isLoading={vendorSelectLoading}
                        disabled={vendorSelectLoading}
                        stackKey={COMPONENT_LIBRARY_STACK.supplier}
                        openStackKey={openSelectKey}
                        onStackKeyChange={onOpenSelectKeyChange}
                    />
                </div>
                <FormTextInput
                    label="Notes"
                    value={draft.notes}
                    onChange={(value) => onPatchDraft({ notes: value })}
                    placeholder="e.g. Price increase of $0.01 - BS - 3/27/26"
                />
            </div>
            <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={isSaving}
                        className="inline-flex relative h-9 items-center rounded-md bg-gray-900 px-4 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {isSaving && (
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
                                <LoaderSpinner width="14" height="14" colorSpinner="white" border="2" />
                            </div>
                        )}
                        <span>Save</span>
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isSaving}
                        className="inline-flex h-9 items-center rounded-md px-3 text-sm text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </InlineSectionShell>
    );
}

function ComponentsSection({
    items,
    search,
    onSearchChange,
    editorState,
    draft,
    libraryIsLoading,
    onPatchDraft,
    onCreate,
    onEdit,
    onDelete,
    onSave,
    onCancel,
    allowCreate = true,
    createActionLabel = "Add",
    vendorOptions = [],
    vendorSelectLoading = false,
    openSelectKey = null,
    onOpenSelectKeyChange,
    isSaving = false,
    usageCounts = {},
    onOpenUsageModal,
}) {
    if (editorState !== "list") {
        return (
            <ComponentEditorSection
                draft={draft}
                onPatchDraft={onPatchDraft}
                onSave={onSave}
                onCancel={onCancel}
                vendorOptions={vendorOptions}
                vendorSelectLoading={vendorSelectLoading}
                openSelectKey={openSelectKey}
                onOpenSelectKeyChange={onOpenSelectKeyChange}
                isSaving={isSaving}
            />
        );
    }

    return (
        <div className="flex h-full flex-col bg-white">
            <SectionHeader
                countLabel={`${items.length} component preset${items.length === 1 ? "" : "s"}`}
                search={search}
                onSearchChange={onSearchChange}
                searchPlaceholder="Search component presets..."
                actionLabel={allowCreate ? createActionLabel : null}
                onAction={allowCreate ? onCreate : null}
                isComponent={true}
            />
            {libraryIsLoading ? (
                <div className="flex h-full items-center justify-center bg-white">
                    <LoaderSpinner width="32" height="32" border="3" colorSpinner="black" />
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-[1fr_96px_75px] gap-x-3 border-b border-gray-200 bg-gray-50 px-4 py-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Component</span>
                        <span className="text-right text-[11px] font-semibold uppercase tracking-wide text-gray-400">Cost</span>
                        <span />
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {items.length ? (
                            items.map((item) => (
                                <div
                                    key={item.id}
                                    className="group grid grid-cols-[1fr_96px_72px] items-center gap-x-3 border-b border-gray-100 px-4 py-3 transition hover:bg-gray-50"
                                >
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-gray-900">{item.name}</p>
                                        <p className="truncate text-sm text-gray-400">{item.type || "No type yet"}</p>
                                    </div>
                                    <div className="text-right text-sm font-semibold text-gray-900">
                                        {Number(item.defaultUnitCost) ? `$${Number(item.defaultUnitCost).toFixed(Number(item.defaultUnitCost) < 0.1 ? 4 : 2)}` : "—"}
                                    </div>
                                    {(() => {
                                        const usageCount = Number(usageCounts?.[String(item.id)] ?? 0);
                                        const inUse = usageCount > 0;
                                        const componentDeleteTooltipId = `delete-bom-component-tooltip-${item.id}`;
                                        return (
                                    <div
                                        className={`flex items-center justify-end gap-1 transition-opacity ${
                                            inUse ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => onEdit(item)}
                                            className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                                        >
                                            <PencilSquareIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                            type="button"
                                            title={inUse ? undefined : "Delete"}
                                            data-tip={inUse}
                                            data-for={componentDeleteTooltipId}
                                            aria-label={
                                                inUse ? "Cannot delete — in use by products" : "Delete component preset"
                                            }
                                            onClick={() => {
                                                if (inUse) {
                                                    onOpenUsageModal?.({
                                                        kind: "component_preset",
                                                        id: item.id,
                                                        label: item.name,
                                                    });
                                                    return;
                                                }
                                                onDelete(item.id);
                                            }}
                                            className={`rounded p-1 transition-all duration-200 ${
                                                inUse
                                                    ? "text-gray-400 cursor-not-allowed opacity-60"
                                                    : "text-gray-300 hover:bg-gray-100 hover:text-red-500"
                                            }`}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                        {inUse ? (
                                            <ReactTooltip
                                                id={componentDeleteTooltipId}
                                                effect="solid"
                                                place="top"
                                                className="text-xs"
                                            >
                                                Cannot be deleted. Click for details.
                                            </ReactTooltip>
                                        ) : null}
                                    </div>
                                        );
                                    })()}
                                </div>
                            ))
                        ) : (
                            <div className="px-4 py-10 text-center text-sm text-gray-500">No component presets found.</div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function emptyPresetComponent() {
    return {
        id: `preset-component-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        component: "",
        description: "",
        supplier: "",
        vendorId: "",
        qty: 1,
        unitCost: 0,
        extCost: 0,
    };
}

function recalcPresetComponent(component) {
    return {
        ...component,
        qty: Number(component.qty || 0),
        unitCost: Number(component.unitCost || 0),
        extCost: Number(component.qty || 0) * Number(component.unitCost || 0),
    };
}

function createBoxPresetDraft(item = null) {
    return item
        ? {
              id: item.id,
              groupId: item.groupId ?? null,
              source: item.source || "settings",
              name: item.name || "",
              notes: item.notes || "",
              components: Array.isArray(item.components) && item.components.length
                  ? item.components.map((component, index) => ({
                        id: component.id || `preset-component-${item.id}-${index}`,
                        component: component.component || "",
                        description: component.description || "",
                        supplier: component.supplier || "",
                        vendorId:
                            component.vendorId != null
                                ? String(component.vendorId)
                                : component.vendor_id != null
                                  ? String(component.vendor_id)
                                  : "",
                        qty: component.qty || 1,
                        unitCost: component.unitCost || 0,
                        extCost: component.extCost || Number(component.qty || 0) * Number(component.unitCost || 0),
                    }))
                  : [emptyPresetComponent()],
          }
        : {
              id: "",
              groupId: null,
              source: "group",
              name: "",
              notes: "",
              components: [emptyPresetComponent()],
          };
}

function BoxPresetForm({
    draft,
    onChange,
    onSave,
    onCancel,
    title,
    isCreate,
    groupResults,
    groupSearchLoading,
    showGroupResults,
    onSelectGroup,
    onGroupQueryChange,
    vendorOptions,
    vendorSelectLoading,
    openSelectKey,
    onOpenSelectKeyChange,
}) {
    const productInputRef = useRef(null);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const totalCost = (draft.components || []).reduce((sum, component) => sum + Number(component.extCost || 0), 0);

    const updateComponent = (id, key, value) => {
        onChange({
            ...draft,
            components: draft.components.map((component) =>
                component.id === id ? recalcPresetComponent({ ...component, [key]: value }) : component
            ),
        });
    };

    const addComponent = () => {
        onChange({
            ...draft,
            components: [...draft.components, emptyPresetComponent()],
        });
    };

    const removeComponent = (id) => {
        onChange({
            ...draft,
            components: draft.components.filter((component) => component.id !== id),
        });
    };

    return (
        <div className={`border-b border-gray-200 bg-gray-50 ${isCreate ? "p-5" : "p-2"}`}>
            {title && <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{title}</p>}
            <div className="space-y-4">
                <div>
                    <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Product Group *
                    </span>
                    {isCreate ? (
                        <div className="relative">
                            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                            <input
                                ref={productInputRef}
                                value={draft.name}
                                onChange={(e) => {
                                    onGroupQueryChange(e.target.value);
                                    setShowProductDropdown(true);
                                }}
                                onFocus={() => setShowProductDropdown(true)}
                                onBlur={() => setTimeout(() => setShowProductDropdown(false), 150)}
                                placeholder="Search product groups…"
                                className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-8 text-[12px] text-gray-900 outline-none focus:border-gray-400"
                            />
                            {draft.name ? (
                                <button
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        onGroupQueryChange("");
                                        productInputRef.current?.focus();
                                    }}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <XMarkIcon className="h-3 w-3" />
                                </button>
                            ) : null}
                            {showProductDropdown &&
                            (groupSearchLoading || groupResults.length > 0 || showGroupResults) ? (
                                <div className="absolute left-0 right-0 top-full z-50 mt-0.5 max-h-52 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                                    {groupSearchLoading ? (
                                        <div className="flex items-center justify-center gap-2 px-3 py-3 text-[11px] text-gray-400">
                                            <LoaderSpinner width="20" height="20" border="2" colorSpinner="black" />
                                            Searching…
                                        </div>
                                    ) : groupResults.length > 0 ? (
                                        groupResults.map((item) => {
                                            const active =
                                                draft.groupId != null && String(draft.groupId) === String(item.id);
                                            return (
                                                <button
                                                    key={item.id}
                                                    type="button"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => {
                                                        onSelectGroup(item);
                                                        setShowProductDropdown(false);
                                                    }}
                                                    className={`w-full px-3 py-1.5 text-left text-[12px] transition-colors hover:bg-gray-50 ${
                                                        active
                                                            ? "bg-gray-100 font-medium text-gray-900"
                                                            : "text-gray-700"
                                                    }`}
                                                >
                                                    {item.name}
                                                </button>
                                            );
                                        })
                                    ) : String(draft.name || "").trim() ? (
                                        <div className="px-3 py-2 text-[11px] text-gray-400">
                                            No groups match &quot;{draft.name}&quot;
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <div className="flex h-9 items-center rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700">
                            {draft.name || "—"}
                        </div>
                    )}
                    {draft.source === "group" ? (
                        <div className="mt-2 space-y-2">
                            {draft.groupId != null && isCreate ? (
                                <p className="flex items-center gap-1 text-[11px] text-green-600">
                                    <CheckIcon className="h-3 w-3" />
                                    {draft.name} — save will create this box preset for the selected product group
                                </p>
                            ) : null}
                            {!draft.groupId ? (
                                <p className="text-[11px] text-gray-400">
                                    Select a product group to create this box preset.
                                </p>
                            ) : null}
                        </div>
                    ) : null}
                </div>
                <div className="space-y-1.5">
                    <div className="grid grid-cols-[1fr_1fr_1fr_40px_56px_56px_20px] gap-1 px-1 text-[11px] text-gray-400">
                        <span className="w-[100px]">Component</span>
                        <span className="w-[160px]">Description</span>
                        <span className="w-[220px]">Supplier</span>
                        <span className="text-right w-[40px]">Qty</span>
                        <span className="text-right w-[56px]">Cost</span>
                        <span className="text-right w-[56px]">Ext.</span>
                        <span />
                    </div>
                    {draft.components.map((component, index) => (
                        <div
                            key={component.id}
                            className="grid grid-cols-[1fr_1fr_1fr_40px_56px_56px_20px] items-center gap-1"
                        >
                            <input
                                value={component.component}
                                onChange={(e) => updateComponent(component.id, "component", e.target.value)}
                                placeholder="Box, Insert..."
                                className="h-7 w-[100px] rounded border border-gray-200 px-1.5 text-xs"
                            />
                            <input
                                value={component.description}
                                onChange={(e) => updateComponent(component.id, "description", e.target.value)}
                                placeholder="Description..."
                                className="h-7 w-[160px] rounded border border-gray-200 px-1.5 text-xs"
                            />
                            <AddDrawerSelect
                                options={vendorOptions}
                                value={component.vendorId || ""}
                                onChange={(value) => {
                                    const vendor = (vendorOptions || []).find(
                                        (option) => String(option.value) === String(value || "")
                                    );
                                    onChange({
                                        ...draft,
                                        components: draft.components.map((row) =>
                                            row.id === component.id
                                                ? {
                                                      ...row,
                                                      vendorId: value || "",
                                                      supplier: vendor?.label || "",
                                                  }
                                                : row
                                        ),
                                    });
                                }}
                                noneLabel="— Supplier —"
                                emptyLabel="— Supplier —"
                                buttonSelectedClassname="!h-7 !min-h-7 !rounded !border !border-gray-200 !px-1 !py-1 !text-xs"
                                selectClassname="w-[220px]"
                                renderInPortal
                                stackKey={BOX_PRESET_STACK.supplier(index)}
                                openStackKey={openSelectKey}
                                onStackKeyChange={onOpenSelectKeyChange}
                                isLoading={vendorSelectLoading}
                            />
                            <input
                                type="number"
                                value={component.qty}
                                onChange={(e) => updateComponent(component.id, "qty", e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "." || e.key === ",") {
                                        e.preventDefault();
                                    }
                                }}
                                className="h-7 w-[40px] rounded border border-gray-200 px-1 text-right text-xs no-arrows-by-input"
                            />
                            <input
                                type="number"
                                step="0.01"
                                value={component.unitCost}
                                onChange={(e) => updateComponent(component.id, "unitCost", e.target.value)}
                                className="h-7 w-[56px] rounded border border-gray-200 px-1 text-right text-xs no-arrows-by-input"
                            />
                            <span className="text-right text-xs tabular-nums text-gray-600 w-[56px]">
                                ${Number(component.extCost || 0).toFixed(2)}
                            </span>
                            <button
                                type="button"
                                onClick={() => removeComponent(component.id)}
                                className="flex justify-center text-gray-400 hover:text-red-500"
                            >
                                <XMarkIcon className="h-3 w-3" />
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addComponent}
                        className="mt-1 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
                    >
                        <PlusIcon className="h-3 w-3" />
                        Add component
                    </button>
                </div>
                <div className="flex items-center justify-between gap-3">
                    <p className="text-lg font-semibold text-gray-900">Total: ${totalCost.toFixed(2)}</p>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onSave}
                            className="inline-flex h-9 items-center rounded-md bg-gray-900 px-4 text-sm font-medium text-white transition hover:bg-gray-800"
                        >
                            Save Preset
                        </button>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="inline-flex h-9 items-center rounded-md px-3 text-sm text-gray-600 transition hover:bg-gray-100"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function BoxPresetsSection({
    items,
    search,
    onSearchChange,
    expandedIds,
    onToggleExpanded,
    editorState,
    draft,
    onDraftChange,
    onCreate,
    onEdit,
    onDelete,
    onSave,
    onCancel,
    openSelectKey,
    onOpenSelectKeyChange,
    vendorOptions,
    vendorSelectLoading,
    groupResults,
    groupSearchLoading,
    showGroupResults,
    onSelectGroup,
    onGroupQueryChange,
    usageCounts = {},
    onOpenUsageModal,
}) {
    return (
        <div className="flex h-full flex-col bg-white">
            <SectionHeader
                countLabel={`${items.length} preset${items.length === 1 ? "" : "s"}`}
                search={search}
                onSearchChange={onSearchChange}
                searchPlaceholder="Search product..."
                actionLabel="New Preset"
                onAction={onCreate}
            />
            {editorState === "create" ? (
                <BoxPresetForm
                    draft={draft}
                    onChange={onDraftChange}
                    onSave={onSave}
                    onCancel={onCancel}
                    title="New Box Preset"
                    isCreate={true}
                    vendorOptions={vendorOptions}
                    vendorSelectLoading={vendorSelectLoading}
                    openSelectKey={openSelectKey}
                    onOpenSelectKeyChange={onOpenSelectKeyChange}
                    groupResults={groupResults}
                    groupSearchLoading={groupSearchLoading}
                    showGroupResults={showGroupResults}
                    onSelectGroup={onSelectGroup}
                    onGroupQueryChange={onGroupQueryChange}
                />
            ) : null}
            <div className="flex-1 overflow-y-auto">
                {items.length ? (
                    items.map((item) => {
                        const isExpanded = expandedIds.includes(item.id);
                        const totalCost = Number(item.totalCost || 0);
                        const componentCount = Array.isArray(item.components) ? item.components.length : 0;
                        const usageCount =
                            item.source === "group" && item.groupId != null
                                ? Number(usageCounts?.[String(item.groupId)] ?? 0)
                                : 0;
                        const inUse = usageCount > 0;
                        return (
                            <div key={item.id} className="border-b border-gray-100">
                                <div
                                    className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 transition hover:bg-gray-50"
                                    onClick={() => onToggleExpanded(item.id)}
                                >
                                    <div className="flex min-w-0 items-center gap-2">
                                        {isExpanded ? (
                                            <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
                                        ) : (
                                            <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" />
                                        )}
                                        <span className="truncate text-base font-semibold text-gray-900">{item.name}</span>
                                        <span className="shrink-0 text-sm text-gray-400">
                                            {componentCount} {componentCount === 1 ? "item" : "items"}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-lg font-semibold text-gray-900">${totalCost.toFixed(2)}</span>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEdit(item);
                                            }}
                                            className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                                        >
                                            <PencilSquareIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                            type="button"
                                            title={inUse ? undefined : "Delete"}
                                            data-tip={inUse}
                                            data-for={`delete-bom-box-preset-tooltip-${item.groupId}`}
                                            aria-label={
                                                inUse ? "Cannot delete — in use by products" : "Delete box preset"
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (inUse) {
                                                    onOpenUsageModal?.({
                                                        kind: "box_preset_group",
                                                        id: item.groupId,
                                                        label: item.name,
                                                    });
                                                    return;
                                                }
                                                onDelete(item);
                                            }}
                                            className={`rounded p-1 transition-all duration-200 ${
                                                inUse
                                                    ? "text-gray-400 cursor-not-allowed opacity-60"
                                                    : "text-gray-300 hover:bg-gray-100 hover:text-red-500"
                                            }`}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                        {inUse ? (
                                            <ReactTooltip
                                                id={`delete-bom-box-preset-tooltip-${item.groupId}`}
                                                effect="solid"
                                                place="top"
                                                className="text-xs"
                                            >
                                                Cannot be deleted. Click for details.
                                            </ReactTooltip>
                                        ) : null}
                                    </div>
                                </div>
                                {isExpanded ? (
                                    <div className="border-t border-gray-100 bg-gray-50 p-2 px-3">
                                        {editorState === "edit" && draft.id === item.id ? (
                                            <BoxPresetForm
                                                draft={draft}
                                                onChange={onDraftChange}
                                                onSave={onSave}
                                                onCancel={onCancel}
                                                isCreate={false}
                                                vendorOptions={vendorOptions}
                                                vendorSelectLoading={vendorSelectLoading}
                                                openSelectKey={openSelectKey}
                                                onOpenSelectKeyChange={onOpenSelectKeyChange}
                                                groupResults={[]}
                                                groupSearchLoading={false}
                                                showGroupResults={false}
                                                onSelectGroup={() => {}}
                                                onGroupQueryChange={() => {}}
                                            />
                                        ) : componentCount ? (
                                            <div className="overflow-hidden rounded-md">
                                                <table className="min-w-full text-left">
                                                    <thead className="bg-gray-50">
                                                        <tr className="border-b border-gray-200 text-sm tracking-wide text-gray-400">
                                                            <th className="px-3 py-1 font-bold">Component</th>
                                                            <th className="px-3 py-1 font-bold">Description</th>
                                                            <th className="px-3 py-1 font-bold">Supplier</th>
                                                            <th className="px-3 py-1 text-right font-bold">Qty</th>
                                                            <th className="px-3 py-1 text-right font-bold">Cost</th>
                                                            <th className="px-3 py-1 text-right font-bold">Ext.</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {item.components.map((component, index) => (
                                                            <tr key={component.id || `${item.id}-${index}`} className="border-b border-gray-100 last:border-b-0">
                                                                <td className="px-3 py-2 text-sm font-medium text-gray-800">{component.component}</td>
                                                                <td className="px-3 py-2 text-sm text-gray-500">{component.description || "—"}</td>
                                                                <td className="px-3 py-2 text-sm text-gray-500">{component.supplier || "—"}</td>
                                                                <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">{component.qty || 0}</td>
                                                                <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">${Number(component.unitCost || 0).toFixed(2)}</td>
                                                                <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">${Number(component.extCost || 0).toFixed(2)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-500">No components defined yet.</p>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })
                ) : (
                    <div className="px-4 py-10 text-center text-sm text-gray-500">No presets found.</div>
                )}
            </div>
        </div>
    );
}

function createInstructionDraft(item = null) {
    return item
        ? {
              id: item.id,
              groupId: item.groupId ?? null,
              source: item.source || "group",
              name: item.name || "",
              steps: Array.isArray(item.steps) && item.steps.length ? [...item.steps] : [""],
              copyFromId: "",
          }
        : {
              id: "",
              groupId: null,
              source: "group",
              name: "",
              steps: [""],
              copyFromId: "",
          };
}

function StepsListEditor({ steps, onChange }) {
    return (
        <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Steps</p>
            <div className="space-y-2">
                {(steps || []).map((step, index) => (
                    <div key={index} className="flex items-start gap-2">
                        <span className="mt-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                            {index + 1}
                        </span>
                        <textarea
                            rows={2}
                            value={step}
                            onChange={(e) =>
                                onChange(
                                    steps.map((item, stepIndex) => (stepIndex === index ? e.target.value : item))
                                )
                            }
                            placeholder={`Step ${index + 1}...`}
                            className="min-h-[64px] flex-1 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none transition focus:border-gray-400"
                        />
                        <button
                            type="button"
                            onClick={() => onChange(steps.filter((_, stepIndex) => stepIndex !== index))}
                            className="mt-2 rounded p-1 text-gray-300 transition hover:bg-gray-100 hover:text-red-500"
                        >
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>
            <button
                type="button"
                onClick={() => onChange([...(steps || []), ""])}
                className="mt-3 text-sm text-gray-500 transition hover:text-gray-900"
            >
                + Add step
            </button>
        </div>
    );
}

function InstructionPresetForm({
    draft,
    allItems,
    onChange,
    onSave,
    onCancel,
    title,
    showCopyFrom,
    groupResults,
    groupSearchLoading,
    allSearchResultsTaken,
    onSelectGroup,
    onGroupQueryChange,
    isCreate = false,
}) {
    const productInputRef = useRef(null);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    return (
        <div className={` ${isCreate ? "border-b border-gray-200 bg-gray-50 px-7 py-4" : "px-1"}`}>
            <div className="space-y-4">
                <div>
                    <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Product Group *
                    </span>
                    {showCopyFrom ? (
                        <div className="relative">
                            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                            <input
                                ref={productInputRef}
                                value={draft.name}
                                onChange={(e) => {
                                    onGroupQueryChange(e.target.value);
                                    setShowProductDropdown(true);
                                }}
                                onFocus={() => setShowProductDropdown(true)}
                                onBlur={() => setTimeout(() => setShowProductDropdown(false), 150)}
                                placeholder="Type to search products..."
                                className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-8 text-[12px] text-gray-900 outline-none focus:border-gray-400"
                            />
                            {draft.name ? (
                                <button
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        onGroupQueryChange("");
                                        productInputRef.current?.focus();
                                    }}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <XMarkIcon className="h-3 w-3" />
                                </button>
                            ) : null}
                            {showProductDropdown &&
                            (groupSearchLoading || groupResults.length > 0 || allSearchResultsTaken || String(draft.name || "").trim()) ? (
                                <div className="absolute left-0 right-0 top-full z-50 mt-0.5 max-h-52 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                                    {groupSearchLoading ? (
                                        <div className="flex items-center justify-center gap-2 px-3 py-3 text-[11px] text-gray-400">
                                            <LoaderSpinner width="20" height="20" border="2" colorSpinner="black" />
                                            Searching…
                                        </div>
                                    ) : allSearchResultsTaken ? (
                                        <div className="px-3 py-2 text-[11px] text-gray-500">
                                            All matching product groups already have instructions.
                                        </div>
                                    ) : groupResults.length > 0 ? (
                                        groupResults.map((item) => (
                                            <button
                                                key={item.id}
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => {
                                                    onSelectGroup(item);
                                                    setShowProductDropdown(false);
                                                }}
                                                className={`w-full px-3 py-1.5 text-left text-[12px] transition-colors hover:bg-gray-50 ${
                                                    draft.groupId != null && String(draft.groupId) === String(item.id)
                                                        ? "bg-gray-100 font-medium text-gray-900"
                                                        : "text-gray-700"
                                                }`}
                                            >
                                                {item.name}
                                            </button>
                                        ))
                                    ) : (
                                        <div className="px-3 py-2 text-[11px] text-gray-400">
                                            No groups match &quot;{draft.name}&quot;
                                        </div>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <div className="flex h-9 items-center rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700">
                            {draft.name || "—"}
                        </div>
                    )}
                </div>
                {showCopyFrom ? (
                    <label className="block">
                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Copy from existing preset (optional)
                        </span>
                        <select
                            value={draft.copyFromId || ""}
                            onChange={(e) => {
                                const nextId = e.target.value;
                                const source = allItems.find((item) => item.id === nextId);
                                onChange({
                                    ...draft,
                                    copyFromId: nextId,
                                    steps: source ? [...(source.steps || [])] : [""],
                                });
                            }}
                            className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700 outline-none transition focus:border-gray-400"
                        >
                            <option value="">— Start from scratch —</option>
                            {allItems.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                    </label>
                ) : null}
                <StepsListEditor
                    steps={draft.steps}
                    onChange={(steps) => onChange({ ...draft, steps })}
                />
                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="inline-flex h-9 items-center rounded-md border border-gray-200 bg-white px-4 text-sm text-gray-600 transition hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onSave}
                        className="inline-flex h-9 items-center rounded-md bg-gray-900 px-4 text-sm font-medium text-white transition hover:bg-gray-800"
                    >
                        {title}
                    </button>
                </div>
            </div>
        </div>
    );
}

function InstructionsSection({
    items,
    allItems,
    search,
    onSearchChange,
    expandedIds,
    onToggleExpanded,
    editorState,
    draft,
    onDraftChange,
    onCreate,
    onEdit,
    onDelete,
    onSave,
    onCancel,
    groupResults,
    groupSearchLoading,
    allSearchResultsTaken,
    onSelectGroup,
    onGroupQueryChange,
}) {
    return (
        <div className="flex h-full flex-col bg-white">
            <SectionHeader
                countLabel={`${items.length} preset${items.length === 1 ? "" : "s"}`}
                search={search}
                onSearchChange={onSearchChange}
                searchPlaceholder="Search presets..."
                actionLabel="New Preset"
                onAction={onCreate}
            />
            {editorState === "create" ? (
                <InstructionPresetForm
                    draft={draft}
                    allItems={allItems}
                    onChange={onDraftChange}
                    onSave={onSave}
                    onCancel={onCancel}
                    title="Save Preset"
                    showCopyFrom
                    groupResults={groupResults}
                    groupSearchLoading={groupSearchLoading}
                    allSearchResultsTaken={allSearchResultsTaken}
                    onSelectGroup={onSelectGroup}
                    onGroupQueryChange={onGroupQueryChange}
                    isCreate
                />
            ) : null}
            <div className="flex-1 overflow-y-auto">
                {items.length ? (
                    items.map((item) => {
                        const isExpanded = expandedIds.includes(item.id);
                        const steps = Array.isArray(item.steps) ? item.steps : [];
                        return (
                            <div key={item.id} className="border-b border-gray-100">
                                <div
                                    className="flex cursor-pointer items-center gap-2 px-4 py-3 transition hover:bg-gray-50"
                                    onClick={() => onToggleExpanded(item.id)}
                                >
                                    {isExpanded ? (
                                        <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
                                    ) : (
                                        <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" />
                                    )}
                                    <ClipboardDocumentListIcon className="h-4 w-4 shrink-0 text-gray-400" />
                                    <span className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900">{item.name}</span>
                                    <span className="shrink-0 text-sm text-gray-400">
                                        {steps.length} {steps.length === 1 ? "step" : "steps"}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(item);
                                        }}
                                        className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                                    >
                                        <PencilSquareIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete(item);
                                        }}
                                        className="rounded p-1 text-gray-300 transition hover:bg-gray-100 hover:text-red-500"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </div>
                                {isExpanded ? (
                                    <div className="border-t border-gray-100 bg-white px-5 py-3">
                                        {editorState === "edit" && draft.id === item.id ? (
                                            <InstructionPresetForm
                                                draft={draft}
                                                allItems={allItems}
                                                onChange={onDraftChange}
                                                onSave={onSave}
                                                onCancel={onCancel}
                                                title="Save"
                                                showCopyFrom={false}
                                                groupResults={[]}
                                                groupSearchLoading={false}
                                                allSearchResultsTaken={false}
                                                onSelectGroup={() => {}}
                                                onGroupQueryChange={() => {}}
                                                isCreate={false}
                                            />
                                        ) : steps.length ? (
                                            <ol className="space-y-2">
                                                {steps.map((step, index) => (
                                                    <li key={index} className="flex items-start gap-3 text-sm text-gray-700">
                                                        <span className="mt-0.5 shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-900 text-xs font-semibold text-white">
                                                            {index + 1}
                                                        </span>
                                                        <span className="leading-6">{step}</span>
                                                    </li>
                                                ))}
                                            </ol>
                                        ) : (
                                            <p className="text-sm text-gray-500">No steps yet.</p>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })
                ) : (
                    <div className="px-4 py-10 text-center text-sm text-gray-500">No instruction presets found.</div>
                )}
            </div>
        </div>
    );
}

function createComponentDraft(item = null) {
    if (!item) {
        return {
            id: "",
            name: "",
            type: "",
            vendorId: "",
            defaultQty: 1,
            defaultUnitCost: "",
            notes: "",
        };
    }
    const vendorId =
        item.vendorId != null && item.vendorId !== ""
            ? String(item.vendorId)
            : item.vendor_id != null && item.vendor_id !== ""
              ? String(item.vendor_id)
              : "";
    return {
        id: item.id,
        name: item.name || "",
        type: item.type || "",
        vendorId,
        defaultQty: item.defaultQty ?? 1,
        defaultUnitCost:
            item.defaultUnitCost !== undefined && item.defaultUnitCost !== null && item.defaultUnitCost !== ""
                ? String(item.defaultUnitCost)
                : "",
        notes: item.notes || "",
    };
}

const BomSettingsDrawer = () => {
    const dispatch = useDispatch();
    const {
        settingsOpen,
        settingsSection,
        settingsExpandedBoxGroupIds,
        settingsExpandedInstructionGroupIds,
        data,
        loadingKeys = [],
    } =
        useSelector(selectBomUi);
    const [section, setSection] = useState("components");

    const [componentSearch, setComponentSearch] = useState("");
    const [componentEditorState, setComponentEditorState] = useState("list");
    const [componentDraft, setComponentDraft] = useState(createComponentDraft());
    const [componentDeleteTarget, setComponentDeleteTarget] = useState(null);
    const [componentDeleteLoading, setComponentDeleteLoading] = useState(false);
    const [componentOpenSelectKey, setComponentOpenSelectKey] = useState(null);
    const [usageCounts, setUsageCounts] = useState(emptyBomUsageCounts());
    const [usageModal, setUsageModal] = useState(null);
    const [usageSkus, setUsageSkus] = useState([]);
    const [usageTotal, setUsageTotal] = useState(0);
    const [usageSkusLoading, setUsageSkusLoading] = useState(false);

    const [boxSearch, setBoxSearch] = useState("");
    const [boxExpandedIds, setBoxExpandedIds] = useState([]);
    const [boxEditorState, setBoxEditorState] = useState("list");
    const [boxDraft, setBoxDraft] = useState(createBoxPresetDraft());
    const [boxGroupResults, setBoxGroupResults] = useState([]);
    const [boxGroupSearchLoading, setBoxGroupSearchLoading] = useState(false);
    const [boxOpenSelectKey, setBoxOpenSelectKey] = useState(null);
    const [boxDeleteTarget, setBoxDeleteTarget] = useState(null);
    const [boxDeleteLoading, setBoxDeleteLoading] = useState(false);

    const [instructionSearch, setInstructionSearch] = useState("");
    const [instructionExpandedIds, setInstructionExpandedIds] = useState([]);
    const [instructionEditorState, setInstructionEditorState] = useState("list");
    const [instructionDraft, setInstructionDraft] = useState(createInstructionDraft());
    const [instructionGroupResults, setInstructionGroupResults] = useState([]);
    const [instructionGroupSearchLoading, setInstructionGroupSearchLoading] = useState(false);
    const [instructionDeleteTarget, setInstructionDeleteTarget] = useState(null);
    const [instructionDeleteLoading, setInstructionDeleteLoading] = useState(false);

    const componentItems = data.componentLibrary || [];
    const { vendorsToSelect, loadingKeys: groupLoadingKeys } = useSelector((state) => state.productGroup);
    const boxPresetItems = useMemo(() => {
        const localItems = (data.boxPresets || []).map((item) => ({
            ...item,
            groupId: item.groupId ?? null,
            source: "settings",
        }));
        const groupItems = (data.packagingBoxPresetOptions || []).map((item) => ({
            ...item,
            groupId: item.groupId ?? null,
            source: "group",
        }));
        return [...groupItems, ...localItems].sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })
        );
    }, [data.boxPresets, data.packagingBoxPresetOptions]);
    const instructionItems = useMemo(
        () =>
            (data.packagingInstructionPresetOptions || []).map((item) => ({
                ...item,
                source: "group",
            })),
        [data.packagingInstructionPresetOptions]
    );
    const vendorOptions = useMemo(
        () =>
            (vendorsToSelect || []).map((vendor) => ({
                value: String(vendor.value),
                label: vendor.label,
            })),
        [vendorsToSelect]
    );
    const vendorSelectLoading = groupLoadingKeys.includes(ProductGroupLoadingKeys.getSuppliers);
    const debouncedBoxGroupSearch = useDebounce(boxDraft.name, 350);
    const debouncedInstructionGroupSearch = useDebounce(instructionDraft.name, 350);
    const componentLibraryLoading = loadingKeys.includes(BomUiLoadingKeys.fetchComponentLibrary);
    const componentSaveLoading = loadingKeys.includes(BomUiLoadingKeys.saveComponentLibraryItem);
    const bomUsageSignature = useMemo(
        () =>
            JSON.stringify({
                components: (componentItems || []).map((item) => item?.id),
                boxes: (boxPresetItems || []).map((item) => item?.groupId ?? item?.id),
            }),
        [boxPresetItems, componentItems]
    );

    const filteredComponents = useMemo(() => {
        const query = componentSearch.trim().toLowerCase();
        if (!query) return componentItems;
        return componentItems.filter((item) =>
            [item.name, item.type, item.defaultSupplier, item.notes]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(query)
        );
    }, [componentItems, componentSearch]);

    const filteredBoxPresets = useMemo(() => {
        const query = boxSearch.trim().toLowerCase();
        if (!query) return boxPresetItems;
        return boxPresetItems.filter((item) =>
            [item.name, item.notes, ...(item.components || []).map((component) => component.component)]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(query)
        );
    }, [boxPresetItems, boxSearch]);

    useEffect(() => {
        if (section !== "presets" || boxEditorState !== "create" || boxDraft.source !== "group") {
            setBoxGroupResults([]);
            setBoxGroupSearchLoading(false);
            return undefined;
        }
        const query = String(debouncedBoxGroupSearch || "").trim();
        if (!query) {
            setBoxGroupResults([]);
            setBoxGroupSearchLoading(false);
            return undefined;
        }
        let cancelled = false;
        setBoxGroupSearchLoading(true);
        dispatch(searchBomProductGroups({ search: query, limit: 12 }))
            .unwrap()
            .then((payload) => {
                if (cancelled) return;
                const items = Array.isArray(payload?.items) ? payload.items : [];
                const selectedExists =
                    boxDraft.groupId != null &&
                    items.some((item) => String(item.id) === String(boxDraft.groupId));
                setBoxGroupResults(
                    selectedExists || boxDraft.groupId == null
                        ? items
                        : [{ id: boxDraft.groupId, name: boxDraft.name }, ...items]
                );
            })
            .catch(() => {
                if (!cancelled) setBoxGroupResults([]);
            })
            .finally(() => {
                if (!cancelled) setBoxGroupSearchLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [boxDraft.groupId, boxDraft.name, boxDraft.source, boxEditorState, debouncedBoxGroupSearch, dispatch, section]);

    useEffect(() => {
        if (section !== "instructions" || instructionEditorState !== "create" || instructionDraft.source !== "group") {
            setInstructionGroupResults([]);
            setInstructionGroupSearchLoading(false);
            return undefined;
        }
        const query = String(debouncedInstructionGroupSearch || "").trim();
        if (!query) {
            setInstructionGroupResults([]);
            setInstructionGroupSearchLoading(false);
            return undefined;
        }
        let cancelled = false;
        setInstructionGroupSearchLoading(true);
        dispatch(searchBomProductGroups({ search: query, limit: 12 }))
            .unwrap()
            .then((payload) => {
                if (cancelled) return;
                const items = Array.isArray(payload?.items) ? payload.items : [];
                const selectedExists =
                    instructionDraft.groupId != null &&
                    items.some((item) => String(item.id) === String(instructionDraft.groupId));
                setInstructionGroupResults(
                    selectedExists || instructionDraft.groupId == null
                        ? items
                        : [{ id: instructionDraft.groupId, name: instructionDraft.name }, ...items]
                );
            })
            .catch(() => {
                if (!cancelled) setInstructionGroupResults([]);
            })
            .finally(() => {
                if (!cancelled) setInstructionGroupSearchLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [
        debouncedInstructionGroupSearch,
        dispatch,
        instructionDraft.groupId,
        instructionDraft.name,
        instructionDraft.source,
        instructionEditorState,
        section,
    ]);

    const filteredInstructionPresets = useMemo(() => {
        const query = instructionSearch.trim().toLowerCase();
        if (!query) return instructionItems;
        return instructionItems.filter((item) =>
            [item.name, ...(item.steps || [])]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(query)
        );
    }, [instructionItems, instructionSearch]);

    const reservedInstructionGroupIds = useMemo(() => {
        const set = new Set();
        for (const item of instructionItems || []) {
            if (item?.groupId != null && item.groupId !== "") {
                set.add(String(item.groupId));
            }
        }
        return set;
    }, [instructionItems]);

    const visibleInstructionGroupResults = useMemo(
        () =>
            (instructionGroupResults || []).filter((item) => {
                const id = item?.id;
                if (id == null) return false;
                return !reservedInstructionGroupIds.has(String(id));
            }),
        [instructionGroupResults, reservedInstructionGroupIds]
    );

    const allInstructionSearchResultsTaken = useMemo(
        () =>
            Boolean(
                instructionDraft.name.trim() &&
                    !instructionGroupSearchLoading &&
                    (instructionGroupResults || []).length > 0 &&
                    visibleInstructionGroupResults.length === 0
            ),
        [
            instructionDraft.name,
            instructionGroupResults,
            instructionGroupSearchLoading,
            visibleInstructionGroupResults.length,
        ]
    );

    useEffect(() => {
        if (settingsOpen) {
            dispatch(fetchPackagingBomUi());
            dispatch(fetchBomComponentLibrary());
        }
    }, [dispatch, settingsOpen]);

    useEffect(() => {
        if (!settingsOpen) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(BOM_USAGE_COUNTS_URL);
                if (!cancelled && data) {
                    setUsageCounts({
                        ...emptyBomUsageCounts(),
                        ...data,
                    });
                }
            } catch {
                if (!cancelled) {
                    setUsageCounts(emptyBomUsageCounts());
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [bomUsageSignature, settingsOpen]);

    useEffect(() => {
        if (!settingsOpen) return;
        setSection(settingsSection || "components");
    }, [settingsOpen, settingsSection]);

    useEffect(() => {
        if (!settingsOpen || settingsSection !== "presets") return;
        const requested = new Set((settingsExpandedBoxGroupIds || []).map((id) => String(id)));
        if (requested.size === 0) {
            setBoxExpandedIds([]);
            return;
        }
        setBoxExpandedIds(
            boxPresetItems
                .filter((item) => item.groupId != null && requested.has(String(item.groupId)))
                .map((item) => item.id)
        );
    }, [boxPresetItems, settingsExpandedBoxGroupIds, settingsOpen, settingsSection]);

    useEffect(() => {
        if (!settingsOpen || settingsSection !== "instructions") return;
        const requested = new Set((settingsExpandedInstructionGroupIds || []).map((id) => String(id)));
        if (requested.size === 0) {
            setInstructionExpandedIds([]);
            return;
        }
        setInstructionExpandedIds(
            instructionItems
                .filter((item) => item.groupId != null && requested.has(String(item.groupId)))
                .map((item) => item.id)
        );
    }, [instructionItems, settingsExpandedInstructionGroupIds, settingsOpen, settingsSection]);

    useEffect(() => {
        dispatch(getSuppliers({}));
    }, [dispatch]);

    const openUsageModal = async ({ kind, id, label }) => {
        setUsageModal({ kind, id, label });
        setUsageSkus([]);
        setUsageTotal(0);
        setUsageSkusLoading(true);
        try {
            const { data } = await axios.get(BOM_USAGE_SKUS_URL, {
                params: { kind, id },
            });
            setUsageSkus(data?.skus || []);
            setUsageTotal(Number(data?.total) || 0);
        } catch {
            setUsageSkus([]);
            setUsageTotal(0);
        } finally {
            setUsageSkusLoading(false);
        }
    };

    const closeUsageModal = () => {
        setUsageModal(null);
        setUsageSkus([]);
        setUsageTotal(0);
        setUsageSkusLoading(false);
    };

    const handleUsageSkuPick = (sku) => {
        closeUsageModal();
        dispatch(setBomSettingsOpen(false));
        dispatch(setActiveBomTab("product"));
        dispatch(clearBomFilters("product"));
        dispatch(clearBomRowSelection("product"));
        dispatch(setBomFilter({ tab: "product", key: "search", value: String(sku || "").trim() }));
    };

    const closeDrawer = () => {
        closeUsageModal();
        dispatch(setBomSettingsOpen(false));
        dispatch(fetchProductBomUi());
        dispatch(fetchBundleBomUi());
        dispatch(fetchPackagingBomUi());
    };

    const saveComponent = async () => {
        const name = String(componentDraft.name || "").trim();
        if (!name) {
            toast.error("Name is required", { position: "bottom-right" });
            return;
        }

        const v = componentDraft.vendorId;
        const vendorIdForSave =
            v !== undefined && v !== null && String(v).trim() !== "" ? String(v).trim() : null;
        const isCreate = !componentDraft.id;

        try {
            const payload = {
                component: name,
                description: String(componentDraft.type || "").trim() || null,
                qty: Number(componentDraft.defaultQty) || 0,
                cost: parseComponentCostInput(componentDraft.defaultUnitCost),
                notes: String(componentDraft.notes || "").trim() || null,
                vendorId: vendorIdForSave,
            };
            if (isCreate) {
                await dispatch(createBomComponentLibraryItem(payload)).unwrap();
            } else {
                await dispatch(
                    saveBomComponentLibraryItem({
                        id: componentDraft.id,
                        ...payload,
                    })
                ).unwrap();
            }
            toast.success(isCreate ? "Component preset created" : "Component preset saved", { position: "bottom-right" });
            setComponentEditorState("list");
            setComponentDraft(createComponentDraft());
            setComponentOpenSelectKey(null);
        } catch (error) {
            const message =
                typeof error === "string" ? error : error?.message || "Failed to save component preset";
            toast.error(message, { position: "bottom-right" });
        }
    };

    const confirmDeleteComponent = async () => {
        if (!componentDeleteTarget?.id) return;
        setComponentDeleteLoading(true);
        try {
            await dispatch(deleteBomComponentLibraryItem({ id: componentDeleteTarget.id })).unwrap();
            toast.success(`Component "${componentDeleteTarget.name}" deleted`, { position: "bottom-right" });
            setComponentDeleteTarget(null);
            if (componentDraft.id === componentDeleteTarget.id) {
                setComponentEditorState("list");
                setComponentDraft(createComponentDraft());
            }
        } catch (error) {
            const message =
                typeof error === "string" ? error : error?.message || "Failed to delete component preset";
            toast.error(message, { position: "bottom-right" });
        } finally {
            setComponentDeleteLoading(false);
        }
    };

    const updateBoxGroupQuery = (value) => {
        setBoxDraft((prev) => ({
            ...prev,
            name: value,
            groupId: prev.source === "group" ? null : prev.groupId,
        }));
    };

    const updateInstructionGroupQuery = (value) => {
        setInstructionDraft((prev) => ({
            ...prev,
            name: value,
            groupId: prev.source === "group" ? null : prev.groupId,
        }));
    };

    const saveBoxPreset = async () => {
        if (!String(boxDraft.name || "").trim()) return;
        const components = (boxDraft.components || [])
            .filter((component) => String(component.component || "").trim())
            .map((component) => recalcPresetComponent(component));
        const presetName = String(boxDraft.name || "").trim();
        const isEdit = Boolean(boxDraft.id);
        const today = new Date().toISOString().split("T")[0];
        try {
            if (boxDraft.source === "group") {
                if (boxDraft.groupId == null || !components.length) return;
                await dispatch(
                    savePackagingBom({
                        group_id: boxDraft.groupId,
                        effective_date: isEdit ? undefined : today,
                        components: components.map((component) => ({
                            id: component.boxPresetId || component.box_preset_id || component.id || null,
                            boxPresetId: component.boxPresetId || component.box_preset_id || component.id || null,
                            box_preset_id: component.boxPresetId || component.box_preset_id || component.id || null,
                            component: String(component.component || "").trim(),
                            description: String(component.description || "").trim(),
                            supplier: String(component.supplier || "").trim(),
                            vendorId: component.vendorId ? String(component.vendorId).trim() : "",
                            qty: Number(component.qty || 0),
                            unitCost: Number(component.unitCost || 0),
                        })),
                    })
                ).unwrap();
                await dispatch(fetchPackagingBomUi()).unwrap();
                toast.success(
                    isEdit
                        ? `Box preset for "${presetName}" updated`
                        : `Box preset for "${presetName}" created`,
                    { position: "bottom-right" }
                );
                setBoxEditorState("list");
                setBoxDraft(createBoxPresetDraft());
                setBoxOpenSelectKey(null);
                return;
            }
            const payload = {
                id: boxDraft.id || `box-preset-${Date.now()}`,
                name: presetName,
                notes: String(boxDraft.notes || "").trim(),
                components: components.map((component) => ({
                    ...component,
                    vendorId: component.vendorId ? String(component.vendorId).trim() : "",
                })),
                totalCost: components.reduce((sum, component) => sum + Number(component.extCost || 0), 0),
            };
            dispatch(boxDraft.id ? updateBomBoxPreset(payload) : addBomBoxPreset(payload));
            toast.success(
                isEdit ? `Box preset "${presetName}" updated` : `Box preset "${presetName}" created`,
                { position: "bottom-right" }
            );
            setBoxEditorState("list");
            setBoxDraft(createBoxPresetDraft());
            setBoxOpenSelectKey(null);
        } catch (error) {
            const message =
                typeof error === "string" ? error : error?.message || "Failed to save box preset";
            toast.error(message, { position: "bottom-right" });
        }
    };

    const confirmDeleteBoxPreset = async () => {
        if (!boxDeleteTarget) return;
        const target = boxDeleteTarget;
        setBoxDeleteLoading(true);
        try {
            if (target.groupId != null) {
                await dispatch(deletePackagingBom({ group_ids: [target.groupId] })).unwrap();
                await dispatch(fetchPackagingBomUi()).unwrap();
            } else {
                dispatch(deleteBomBoxPresets([target.id]));
            }
            toast.success(`Box preset "${target.name}" deleted`, { position: "bottom-right" });
            if (boxDraft.id === target.id) {
                setBoxEditorState("list");
                setBoxDraft(createBoxPresetDraft());
                setBoxOpenSelectKey(null);
            }
            setBoxExpandedIds((prev) => prev.filter((id) => id !== target.id));
            setBoxDeleteTarget(null);
        } catch (error) {
            const message =
                typeof error === "string" ? error : error?.message || "Failed to delete box preset";
            toast.error(message, { position: "bottom-right" });
        } finally {
            setBoxDeleteLoading(false);
        }
    };

    const saveInstructionPreset = async () => {
        const filteredSteps = (instructionDraft.steps || []).map((step) => String(step || "").trim()).filter(Boolean);
        const presetName = String(instructionDraft.name || "").trim();
        if (!presetName || !filteredSteps.length || instructionDraft.groupId == null) return;
        try {
            await dispatch(
                savePackagingInstructions({
                    group_id: instructionDraft.groupId,
                    steps: filteredSteps,
                })
            ).unwrap();
            await dispatch(fetchPackagingBomUi()).unwrap();
            toast.success(
                instructionEditorState === "edit"
                    ? `Instruction preset for "${presetName}" updated`
                    : `Instruction preset for "${presetName}" created`,
                { position: "bottom-right" }
            );
            setInstructionEditorState("list");
            setInstructionDraft(createInstructionDraft());
        } catch (error) {
            const message =
                typeof error === "string" ? error : error?.message || "Failed to save instruction preset";
            toast.error(message, { position: "bottom-right" });
        }
    };

    const confirmDeleteInstructionPreset = async () => {
        if (!instructionDeleteTarget?.groupId) return;
        const item = instructionDeleteTarget;
        setInstructionDeleteLoading(true);
        try {
            await dispatch(deletePackagingInstructions({ group_ids: [item.groupId] })).unwrap();
            await dispatch(fetchPackagingBomUi()).unwrap();
            toast.success(`Instruction preset for "${item.name}" deleted`, { position: "bottom-right" });
            if (instructionDraft.id === item.id) {
                setInstructionEditorState("list");
                setInstructionDraft(createInstructionDraft());
            }
            setInstructionExpandedIds((prev) => prev.filter((expandedId) => expandedId !== item.id));
            setInstructionDeleteTarget(null);
        } catch (error) {
            const message =
                typeof error === "string" ? error : error?.message || "Failed to delete instruction preset";
            toast.error(message, { position: "bottom-right" });
        } finally {
            setInstructionDeleteLoading(false);
        }
    };

    return (
        <>
            <Drawer
                isOpen={settingsOpen}
                onClose={closeDrawer}
                title="BOM Settings"
                width="720px"
                headerContent={
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">BOM Settings</h2>
                        <p className="mt-1 text-sm text-gray-500">
                        Manage component library, box presets, and instruction presets used across all BOM types.
                        </p>
                    </div>
                }
            >
                <div className="flex h-full flex-col bg-white">
                    <SectionTabBar section={section} onChange={setSection} />
                    <div className="min-h-0 flex-1 overflow-hidden">
                        {section === "components" ? (
                                <ComponentsSection
                                    items={filteredComponents}
                                    search={componentSearch}
                                    onSearchChange={setComponentSearch}
                                    editorState={componentEditorState}
                                    draft={componentDraft}
                                    libraryIsLoading={componentLibraryLoading}
                                    onPatchDraft={(patch) =>
                                        setComponentDraft((prev) => ({ ...prev, ...patch }))
                                    }
                                    onCreate={() => {
                                        setComponentEditorState("create");
                                        setComponentDraft(createComponentDraft());
                                    }}
                                    onEdit={(item) => {
                                        setComponentEditorState("edit");
                                        setComponentDraft(createComponentDraft(item));
                                    }}
                                    onDelete={(id) => {
                                        const item = filteredComponents.find((component) => component.id === id);
                                        if (item) {
                                            setComponentDeleteTarget(item);
                                        }
                                    }}
                                    onSave={saveComponent}
                                    onCancel={() => {
                                        setComponentEditorState("list");
                                        setComponentDraft(createComponentDraft());
                                        setComponentOpenSelectKey(null);
                                    }}
                                    allowCreate={true}
                                    createActionLabel="Add"
                                    vendorOptions={vendorOptions}
                                    vendorSelectLoading={vendorSelectLoading}
                                    openSelectKey={componentOpenSelectKey}
                                    onOpenSelectKeyChange={setComponentOpenSelectKey}
                                    isSaving={componentSaveLoading}
                                usageCounts={usageCounts.component_preset}
                                onOpenUsageModal={openUsageModal}
                                />
                        ) : null}

                        {section === "presets" ? (
                            <BoxPresetsSection
                                items={filteredBoxPresets}
                                search={boxSearch}
                                onSearchChange={setBoxSearch}
                            expandedIds={boxExpandedIds}
                            onToggleExpanded={(id) =>
                                setBoxExpandedIds((prev) =>
                                    prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
                                )
                            }
                                editorState={boxEditorState}
                                draft={boxDraft}
                                onDraftChange={setBoxDraft}
                                onCreate={() => {
                                    setBoxEditorState("create");
                                    setBoxDraft(createBoxPresetDraft());
                                setBoxExpandedIds([]);
                                    setBoxOpenSelectKey(null);
                                }}
                                onEdit={(item) => {
                                    setBoxEditorState("edit");
                                    setBoxDraft(createBoxPresetDraft(item));
                                setBoxExpandedIds((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));
                                    setBoxOpenSelectKey(null);
                                }}
                                onDelete={setBoxDeleteTarget}
                                onSave={saveBoxPreset}
                                onCancel={() => {
                                    setBoxEditorState("list");
                                    setBoxDraft(createBoxPresetDraft());
                                    setBoxOpenSelectKey(null);
                                }}
                                openSelectKey={boxOpenSelectKey}
                                onOpenSelectKeyChange={setBoxOpenSelectKey}
                                vendorOptions={vendorOptions}
                                vendorSelectLoading={vendorSelectLoading}
                                groupResults={boxGroupResults}
                                groupSearchLoading={boxGroupSearchLoading}
                                showGroupResults={boxEditorState === "create" && Boolean(String(boxDraft.name || "").trim())}
                                usageCounts={usageCounts.box_preset_group}
                                onOpenUsageModal={openUsageModal}
                                onSelectGroup={(item) =>
                                    setBoxDraft((prev) => ({
                                        ...prev,
                                        groupId: item.id,
                                        name: item.name,
                                        source: "group",
                                    }))
                                }
                                onGroupQueryChange={updateBoxGroupQuery}
                            />
                        ) : null}

                        {section === "instructions" ? (
                            <InstructionsSection
                                items={filteredInstructionPresets}
                                allItems={instructionItems}
                                search={instructionSearch}
                                onSearchChange={setInstructionSearch}
                                expandedIds={instructionExpandedIds}
                                onToggleExpanded={(id) =>
                                    setInstructionExpandedIds((prev) =>
                                        prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
                                    )
                                }
                                editorState={instructionEditorState}
                                draft={instructionDraft}
                                onDraftChange={setInstructionDraft}
                                onCreate={() => {
                                    setInstructionEditorState("create");
                                    setInstructionDraft(createInstructionDraft());
                                    setInstructionExpandedIds([]);
                                }}
                                onEdit={(item) => {
                                    setInstructionEditorState("edit");
                                    setInstructionDraft(createInstructionDraft(item));
                                    setInstructionExpandedIds((prev) =>
                                        prev.includes(item.id) ? prev : [...prev, item.id]
                                    );
                                }}
                                onDelete={setInstructionDeleteTarget}
                                onSave={saveInstructionPreset}
                                onCancel={() => {
                                    setInstructionEditorState("list");
                                    setInstructionDraft(createInstructionDraft());
                                }}
                                groupResults={visibleInstructionGroupResults}
                                groupSearchLoading={instructionGroupSearchLoading}
                                allSearchResultsTaken={allInstructionSearchResultsTaken}
                                onSelectGroup={(item) =>
                                    setInstructionDraft((prev) => ({
                                        ...prev,
                                        groupId: item.id,
                                        name: item.name,
                                        source: "group",
                                    }))
                                }
                                onGroupQueryChange={updateInstructionGroupQuery}
                            />
                        ) : null}
                    </div>
                </div>
            </Drawer>
            {usageModal
                ? createPortal(
                      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                          <button
                              type="button"
                              className="absolute inset-0 bg-black/40"
                              aria-label="Close"
                              onClick={closeUsageModal}
                          />
                          <div
                              role="dialog"
                              aria-modal="true"
                              aria-labelledby="bom-usage-modal-title"
                              className="relative z-[101] w-full max-w-md rounded-lg border border-gray-200 bg-white shadow-xl"
                          >
                              <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-4 py-3">
                                  <div className="min-w-0">
                                      <p id="bom-usage-modal-title" className="text-sm font-semibold text-gray-900">
                                          Products using this preset
                                      </p>
                                      <p className="mt-0.5 truncate text-xs text-gray-500">{usageModal.label}</p>
                                  </div>
                                  <button
                                      type="button"
                                      onClick={closeUsageModal}
                                      className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                  >
                                      <XMarkIcon className="h-5 w-5" />
                                  </button>
                              </div>
                              <div className="border-b border-gray-100 bg-gray-50/90 px-4 py-3">
                                  <p className="text-xs text-gray-600">
                                      Click a SKU to close settings and open the Product BOM tab filtered to that product.
                                  </p>
                              </div>
                              <div className="max-h-80 overflow-y-auto px-2 py-2">
                                  {usageSkusLoading ? (
                                      <p className="px-2 py-6 text-center text-sm text-gray-400">Loading SKUs…</p>
                                  ) : usageSkus.length === 0 ? (
                                      <p className="px-2 py-6 text-center text-sm text-gray-400">No SKUs found.</p>
                                  ) : (
                                      <ul className="flex flex-col gap-0.5">
                                          {usageSkus.map((sku) => (
                                              <li key={sku}>
                                                  <button
                                                      type="button"
                                                      className="w-full rounded-md px-3 py-2 text-left font-mono text-sm text-gray-800 hover:bg-primary/10 hover:text-primary"
                                                      onClick={() => handleUsageSkuPick(sku)}
                                                  >
                                                      {sku}
                                                  </button>
                                              </li>
                                          ))}
                                      </ul>
                                  )}
                                  {!usageSkusLoading && usageTotal > usageSkus.length ? (
                                      <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-400">
                                          Showing {usageSkus.length} of {usageTotal} products (max 500).
                                      </p>
                                  ) : null}
                              </div>
                          </div>
                      </div>,
                      document.body
                  )
                : null}
            <ConfrimModal
                isOpen={Boolean(componentDeleteTarget)}
                isLoading={componentDeleteLoading}
                title={
                    componentDeleteTarget
                        ? `Delete component "${componentDeleteTarget.name}"? This removes a product BOM row.`
                        : ""
                }
                confirmButtonTitle="Delete"
                submitClick={confirmDeleteComponent}
                cancelClick={() => {
                    if (!componentDeleteLoading) setComponentDeleteTarget(null);
                }}
                size="max-w-lg"
            />
            <ConfrimModal
                isOpen={Boolean(boxDeleteTarget)}
                isLoading={boxDeleteLoading}
                title={
                    boxDeleteTarget
                        ? `Delete box preset "${boxDeleteTarget.name}"? This cannot be undone.`
                        : ""
                }
                confirmButtonTitle="Delete"
                submitClick={confirmDeleteBoxPreset}
                cancelClick={() => {
                    if (!boxDeleteLoading) setBoxDeleteTarget(null);
                }}
                size="max-w-lg"
            />
            <ConfrimModal
                isOpen={Boolean(instructionDeleteTarget)}
                isLoading={instructionDeleteLoading}
                title={
                    instructionDeleteTarget
                        ? `Delete instruction preset "${instructionDeleteTarget.name}"? This cannot be undone.`
                        : ""
                }
                confirmButtonTitle="Delete"
                submitClick={confirmDeleteInstructionPreset}
                cancelClick={() => {
                    if (!instructionDeleteLoading) setInstructionDeleteTarget(null);
                }}
                size="max-w-lg"
            />
        </>
    );
};

export default BomSettingsDrawer;
